import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, BookOpen, UserRound } from 'lucide-react';
import { Screen } from '@/app/layout/Screen';
import { useGreeting } from '@/app/layout/AppBar';
import { Button } from '@/components/ui/Button';
import { LoadFailure, PanelSkeleton, StateView } from '@/components/ui/Feedback';
import { Badge, Lead, Meta, MiniIcon, PageTitle, Panel, ProgressBar, Row, RowList } from '@/components/ui/Surface';
import { useBackendCourseStore } from '@/store/backendCourseStore';
import { cleanLessonTitle } from '@/lib/titles';
import { useDoctorProfilesStore } from '@/store/doctorProfilesStore';

/**
 * Экран 07. Главная.
 *
 * Показывает только то, что приходит с сервера: активный профиль, модули и
 * прогресс. Прогресс сервер отдаёт вместе с уроками, а не в самих модулях
 * (`status` у модуля остаётся `not_started` даже при сданных заданиях),
 * поэтому уроки запрашиваются для всех модулей сразу.
 *
 * Состояния: загрузка, ошибка с повтором, пусто, данные.
 */
export function HomeScreen() {
  const navigate = useNavigate();
  const greeting = useGreeting();

  const modules = useBackendCourseStore((state) => state.modules);
  const lessonsByCourse = useBackendCourseStore((state) => state.lessonsByCourse);
  const progressByCourse = useBackendCourseStore((state) => state.progressByCourse);
  const loading = useBackendCourseStore((state) => state.loadingModules);
  const error = useBackendCourseStore((state) => state.error);
  const loadModules = useBackendCourseStore((state) => state.loadModules);
  const loadAllProgress = useBackendCourseStore((state) => state.loadAllProgress);

  const profiles = useDoctorProfilesStore((state) => state.profiles);
  const activeId = useDoctorProfilesStore((state) => state.activeId);
  const loadProfiles = useDoctorProfilesStore((state) => state.load);

  useEffect(() => {
    // Прогресс приходит вместе с уроками, поэтому после модулей запрашиваются
    // и они: поле `status` у модуля не отражает пройденные уроки
    void loadModules().then(() => loadAllProgress());
    void loadProfiles();
  }, [loadModules, loadAllProgress, loadProfiles]);

  const activeProfile = profiles.find((profile) => profile.doctor_profile_id === activeId);

  if (loading && modules.length === 0) {
    return (
      <Screen subtitle={greeting} tab="home">
        <PanelSkeleton count={4} />
      </Screen>
    );
  }

  if (error && modules.length === 0) {
    return (
      <Screen subtitle={greeting} tab="home">
        <LoadFailure title="Данные не загрузились" description={error} onRetry={() => void loadModules()} />
      </Screen>
    );
  }

  const totals = modules.reduce(
    (sum, module) => {
      const progress = progressByCourse[module.course_id];
      return {
        completed: sum.completed + (progress?.completed ?? 0),
        total: sum.total + (progress?.total ?? 0),
      };
    },
    { completed: 0, total: 0 }
  );
  const percent = totals.total > 0 ? Math.round((totals.completed / totals.total) * 100) : 0;

  // Продолжать логично с первого модуля, где остались несданные задания
  const current =
    modules.find((module) => {
      const progress = progressByCourse[module.course_id];
      return !progress || progress.completed < progress.total;
    }) ?? modules[0];

  // И с первого несданного урока в нём: список модуля — лишний шаг, когда
  // понятно, к какому именно заданию человек возвращается
  const nextLesson = current
    ? (lessonsByCourse[current.course_id] ?? []).find((lesson) => !lesson.completed)
    : undefined;

  return (
    <Screen subtitle={greeting} tab="home">
      {activeProfile ? (
        <Panel interactive className="mb-2.5" onClick={() => navigate('/profiles')}>
          <Row
            icon={
              <MiniIcon tone="lime">
                <UserRound className="h-[17px] w-[17px]" aria-hidden />
              </MiniIcon>
            }
            title={activeProfile.profile_name}
            subtitle="Активный профиль"
            chevron
          />
        </Panel>
      ) : null}

      <PageTitle>Бренд врача</PageTitle>
      <Lead>
        {totals.completed > 0
          ? 'Продолжайте с того места, где остановились.'
          : 'Курс открыт. Начните с первого модуля — задания выполняются в диалоге с ассистентом.'}
      </Lead>

      {totals.total > 0 ? (
        <div className="mb-3">
          <ProgressBar value={percent} />
          <Meta>
            Пройдено {totals.completed} из {totals.total} заданий
          </Meta>
        </div>
      ) : null}

      {modules.length === 0 ? (
        <StateView
          icon={<BookOpen className="h-6 w-6" aria-hidden />}
          title="Курс пока не открыт"
          description="Модули появятся, когда откроется доступ к программе."
          primary={
            <Button variant="primary" block onClick={() => void loadModules()}>
              Обновить
            </Button>
          }
        />
      ) : (
        <>
          {current ? (
            <Panel tone="brand" className="mb-2.5">
              <span className="text-[11px] text-white/80">
                {totals.completed > 0 ? 'Продолжить' : 'С чего начать'}
              </span>
              <h2 className="mt-1.5 text-[16px] font-medium text-white">
                {nextLesson ? cleanLessonTitle(nextLesson.title) : current.title}
              </h2>
              {nextLesson ? (
                <p className="mt-1 text-[12px] text-white/80">{current.title}</p>
              ) : null}

              {/*
                Переход сразу к нужному уроку: иначе каждый заход начинается с
                поиска места, на котором остановился, в списке модуля
              */}
              <Button
                variant="secondary"
                block
                className="mt-3"
                iconRight={<ArrowRight className="h-4 w-4" aria-hidden />}
                onClick={() =>
                  navigate(
                    nextLesson
                      ? `/course/lessons/${nextLesson.assignment_id}`
                      : `/course/modules/${current.course_id}`
                  )
                }
              >
                {nextLesson ? 'Перейти к заданию' : 'Открыть уроки'}
              </Button>

              {nextLesson ? (
                <button
                  type="button"
                  onClick={() => navigate(`/course/modules/${current.course_id}`)}
                  className="bd-focus mt-2 w-full text-[12px] text-white/80 underline underline-offset-2"
                >
                  Все уроки модуля
                </button>
              ) : null}
            </Panel>
          ) : null}

          <RowList>
            {modules.map((module) => (
              <div key={module.course_id}>
                <Row
                  title={module.title}
                  subtitle={moduleSubtitle(progressByCourse[module.course_id])}
                  trailing={
                    isModuleDone(progressByCourse[module.course_id]) ? (
                      <Badge tone="success">Пройден</Badge>
                    ) : undefined
                  }
                  chevron={!isModuleDone(progressByCourse[module.course_id])}
                  onClick={() => navigate(`/course/modules/${module.course_id}`)}
                />
              </div>
            ))}
          </RowList>
        </>
      )}
    </Screen>
  );
}

/** Подпись модуля по его прогрессу; пока уроки не загружены — без подписи. */
function moduleSubtitle(progress?: { completed: number; total: number }): string | undefined {
  if (!progress || progress.total === 0) return undefined;
  return `Пройдено ${progress.completed} из ${progress.total}`;
}

function isModuleDone(progress?: { completed: number; total: number }): boolean {
  return Boolean(progress && progress.total > 0 && progress.completed === progress.total);
}
